import React from 'react';
import {
  BookOpen, Zap, Shield, Calculator, FileText,
  TrendingUp, ScanLine, BarChart2, Activity, Brain,
  MessageSquare, Target, PieChart, History, Compass,
  FlaskConical, User, Map, BarChart, Cpu, Layers,
  TrendingDown, CreditCard, Clock, Lock,
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';

// ─── Canvas ───────────────────────────────────────────────────────────────────
const NR   = 22;
const NR_S = 14;
const CW   = 380;
const CH   = 1235;

// ─── Nodes ────────────────────────────────────────────────────────────────────
// time: badge shown beside the node (when to use it)
const NODES = [
  // ── Navigate ────────────────────────────────────────────────────────────────
  { id: 'appguide',     label: 'App Guide',     icon: Map,           path: '/appguide',                       color: '#8b83ff', x: 190, y: 45,   r: NR,   time: 'Start here'   },
  { id: 'ag_j',         label: 'Journey',       icon: Compass,       path: '/appguide?tab=start',             color: '#8b83ff', x: 75,  y: 108,  r: NR_S },
  { id: 'ag_tp',        label: 'Trade Plan',    icon: Target,        path: '/trade-planning',                 color: '#f59e0b', x: 190, y: 108,  r: NR_S },
  { id: 'ag_tw',        label: 'Decision Eng',  icon: Brain,         path: '/trade-walkthrough',              color: '#06b6d4', x: 305, y: 108,  r: NR_S },

  // ── Foundation ──────────────────────────────────────────────────────────────
  { id: 'learning',     label: 'Learning',      icon: BookOpen,      path: '/learning',                       color: '#6c63ff', x: 190, y: 185,  r: NR,   time: '10 min/day'   },
  { id: 'act',          label: 'ACT Module',    icon: Zap,           path: '/act',                            color: '#6c63ff', x: 110, y: 247,  r: NR,   time: '5 min'        },
  { id: 'docs',         label: 'Docs',          icon: FileText,      path: '/docs',                           color: '#6c63ff', x: 270, y: 247,  r: NR,   time: 'Reference'    },
  { id: 'riskprofile',  label: 'Risk Profile',  icon: Shield,        path: '/profile?section=risk-profile',   color: '#6c63ff', x: 190, y: 305,  r: NR,   time: 'Do once'      },
  { id: 'riskcalc',     label: 'Risk Calc',     icon: Calculator,    path: '/calculator',                     color: '#4ecca3', x: 110, y: 367,  r: NR,   time: 'Pre-trade'    },
  { id: 'capital',      label: 'Capital',       icon: Target,        path: '/capital',                        color: '#4ecca3', x: 270, y: 367,  r: NR,   time: 'Daily'        },

  // ── Research ────────────────────────────────────────────────────────────────
  { id: 'globalcues',   label: 'Global Cues',   icon: TrendingUp,    path: '/global-cues',                    color: '#4ecca3', x: 190, y: 437,  r: NR,   time: '8:45 AM'      },
  { id: 'mktanalysis',  label: 'Mkt Analysis',  icon: Brain,         path: '/market-analysis',                color: '#4ecca3', x: 90,  y: 497,  r: NR,   time: '9:00 AM',     premium: true },
  { id: 'mkt_reg',      label: 'Regime',        icon: BarChart,      path: '/market-analysis',                color: '#4ecca3', x: 24,  y: 480,  r: NR_S, premium: true },
  { id: 'mkt_sim',      label: 'Simulator',     icon: Cpu,           path: '/market-analysis',                color: '#4ecca3', x: 24,  y: 516,  r: NR_S, premium: true },
  { id: 'discover',     label: 'Discover',      icon: Compass,       path: '/discover',                       color: '#4ecca3', x: 290, y: 497,  r: NR,   time: 'Morning'      },
  { id: 'scanners',     label: 'Scanners',      icon: ScanLine,      path: '/analytics',                      color: '#4ecca3', x: 190, y: 557,  r: NR,   time: '9:05 AM'      },
  { id: 'sc_pat',       label: 'Patterns',      icon: TrendingDown,  path: '/analytics',                      color: '#4ecca3', x: 352, y: 540,  r: NR_S },
  { id: 'sc_bt',        label: 'Scan Lab',      icon: FlaskConical,  path: '/analytics',                      color: '#4ecca3', x: 352, y: 576,  r: NR_S },
  { id: 'derivatives',  label: 'Derivatives',   icon: Activity,      path: '/derivatives',                    color: '#4ecca3', x: 100, y: 617,  r: NR,   time: 'Pre-trade',   premium: true },
  { id: 'markets',      label: 'Markets',       icon: BarChart2,     path: '/markets',                        color: '#4ecca3', x: 280, y: 617,  r: NR,   time: 'Pre-trade'    },

  // ── Execute ─────────────────────────────────────────────────────────────────
  { id: 'nyxai',        label: 'Nyx AI',        icon: MessageSquare, path: '/ai-coach',                       color: '#f59e0b', x: 190, y: 687,  r: NR,   time: 'Before entry', premium: true },
  { id: 'trade',        label: 'Trade',         icon: TrendingUp,    path: '/trade',                          color: '#f59e0b', x: 190, y: 757,  r: NR,   time: '9:15 AM+'     },
  { id: 'tr_eq',        label: 'Equity',        icon: TrendingUp,    path: '/trade',                          color: '#f59e0b', x: 50,  y: 740,  r: NR_S },
  { id: 'tr_fo',        label: 'F&O',           icon: Activity,      path: '/derivatives',                    color: '#f59e0b', x: 330, y: 740,  r: NR_S, premium: true },
  { id: 'advorder',     label: 'Adv Order',     icon: Zap,           path: '/trade/order',                    color: '#f59e0b', x: 292, y: 784,  r: NR,   time: 'With SL'      },
  { id: 'orders',       label: 'Orders',        icon: PieChart,      path: '/orders',                         color: '#f59e0b', x: 190, y: 827,  r: NR,   time: 'Live'         },

  // ── Review ──────────────────────────────────────────────────────────────────
  { id: 'tradehistory', label: 'Trade History', icon: History,       path: '/trade-history',                  color: '#f97316', x: 190, y: 897,  r: NR,   time: 'After 3:30'   },
  { id: 'discipline',   label: 'Discipline',    icon: Shield,        path: '/discipline',                     color: '#f97316', x: 100, y: 964,  r: NR,   time: 'Evening'      },
  { id: 'performance',  label: 'Performance',   icon: BarChart2,     path: '/trade-performance',              color: '#f97316', x: 280, y: 964,  r: NR,   time: 'Weekly'       },
  { id: 'analytics',    label: 'Analytics',     icon: PieChart,      path: '/trading-analytics',              color: '#f97316', x: 190, y: 1029, r: NR,   time: 'Weekly',      premium: true },

  // ── Build ───────────────────────────────────────────────────────────────────
  { id: 'backtest',     label: 'Backtest',      icon: FlaskConical,  path: '/backtest',                       color: '#ce93d8', x: 110, y: 1094, r: NR,   time: 'Before scale', premium: true },
  { id: 'algoflows',    label: 'Algo Flows',    icon: Zap,           path: '/algo-flows',                     color: '#ce93d8', x: 270, y: 1094, r: NR,   time: 'When ready',  premium: true },
  { id: 'profile',      label: 'Profile',       icon: User,          path: '/profile',                        color: '#ce93d8', x: 190, y: 1154, r: NR,   time: 'Monthly'      },
  { id: 'pr_sub',       label: 'Subscription',  icon: CreditCard,    path: '/profile?section=subscription',   color: '#ce93d8', x: 84,  y: 1194, r: NR_S },
  { id: 'pr_rep',       label: 'Reports',       icon: FileText,      path: '/profile?section=reports',        color: '#ce93d8', x: 296, y: 1194, r: NR_S, premium: true },
];

// ─── Edges ────────────────────────────────────────────────────────────────────
const EDGES = [
  { f: 'appguide',     t: 'ag_j',          sub: true  },
  { f: 'appguide',     t: 'ag_tp',         sub: true  },
  { f: 'appguide',     t: 'ag_tw',         sub: true  },
  { f: 'appguide',     t: 'learning'                   },
  { f: 'learning',     t: 'act'                       },
  { f: 'learning',     t: 'docs'                      },
  { f: 'learning',     t: 'riskprofile'               },
  { f: 'act',          t: 'riskprofile'               },
  { f: 'riskprofile',  t: 'riskcalc'                  },
  { f: 'riskprofile',  t: 'capital'                   },
  { f: 'riskcalc',     t: 'globalcues'                },
  { f: 'capital',      t: 'globalcues'                },
  { f: 'globalcues',   t: 'mktanalysis'               },
  { f: 'globalcues',   t: 'discover'                  },
  { f: 'globalcues',   t: 'scanners'                  },
  { f: 'mktanalysis',  t: 'mkt_reg',       sub: true  },
  { f: 'mktanalysis',  t: 'mkt_sim',       sub: true  },
  { f: 'scanners',     t: 'sc_pat',        sub: true  },
  { f: 'scanners',     t: 'sc_bt',         sub: true  },
  { f: 'scanners',     t: 'derivatives'               },
  { f: 'scanners',     t: 'markets'                   },
  { f: 'derivatives',  t: 'nyxai'                     },
  { f: 'markets',      t: 'nyxai'                     },
  { f: 'nyxai',        t: 'trade'                     },
  { f: 'trade',        t: 'tr_eq',         sub: true  },
  { f: 'trade',        t: 'tr_fo',         sub: true  },
  { f: 'trade',        t: 'advorder'                  },
  { f: 'trade',        t: 'orders'                    },
  { f: 'orders',       t: 'tradehistory'              },
  { f: 'tradehistory', t: 'discipline'                },
  { f: 'tradehistory', t: 'performance'               },
  { f: 'discipline',   t: 'analytics'                 },
  { f: 'performance',  t: 'analytics'                 },
  { f: 'analytics',    t: 'backtest'                  },
  { f: 'analytics',    t: 'algoflows'                 },
  { f: 'backtest',     t: 'profile'                   },
  { f: 'algoflows',    t: 'profile'                   },
  { f: 'profile',      t: 'pr_sub',        sub: true  },
  { f: 'profile',      t: 'pr_rep',        sub: true  },
];

// ─── Zone bands ───────────────────────────────────────────────────────────────
const ZONES = [
  { label: 'NAVIGATE',   color: '#8b83ff', y1: 0,    y2: 148  },
  { label: 'FOUNDATION', color: '#6c63ff', y1: 148,  y2: 412  },
  { label: 'RESEARCH',   color: '#4ecca3', y1: 412,  y2: 660  },
  { label: 'EXECUTE',    color: '#f59e0b', y1: 660,  y2: 862  },
  { label: 'REVIEW',     color: '#f97316', y1: 862,  y2: 1068 },
  { label: 'BUILD',      color: '#ce93d8', y1: 1068, y2: CH   },
];

// ─── Particles — deterministic "random" scatter ───────────────────────────────
const PARTICLES = Array.from({ length: 38 }, (_, i) => ({
  cx: ((i * 137 + 43) % (CW - 24)) + 12,
  cy: ((i * 211 + 97) % (CH - 24)) + 12,
  r:  0.8 + (i % 4) * 0.45,
  o:  0.05 + (i % 5) * 0.025,
}));

// ─── Helpers ──────────────────────────────────────────────────────────────────
function bez(x1, y1, x2, y2) {
  const my = (y1 + y2) / 2;
  return `M ${x1} ${y1} C ${x1} ${my}, ${x2} ${my}, ${x2} ${y2}`;
}

const nodeMap = Object.fromEntries(NODES.map(n => [n.id, n]));

// Badge side: left nodes → badge right, right nodes → badge left, center → alternate by id hash
function badgeSide(node) {
  if (node.x <= 130) return 'right';
  if (node.x >= 250) return 'left';
  // center — alternate based on y position bracket
  return Math.floor(node.y / 120) % 2 === 0 ? 'left' : 'right';
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AppIndexMap({ navigate }) {
  const { meetsMinPlan } = usePermissions();

  const handleNodeClick = (node) => {
    if (node.premium && !meetsMinPlan('premium')) {
      navigate('/profile?section=subscription');
      return;
    }
    navigate(node.path);
  };

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header ── */}
      <div
        className="rounded-2xl p-4"
        style={{
          background: 'linear-gradient(135deg, #8b83ff14 0%, #ce93d808 100%)',
          border: '1px solid rgba(139,131,255,0.22)',
        }}
      >
        <p className="text-[11px] font-black uppercase tracking-widest mb-1.5" style={{ color: '#8b83ff' }}>
          App Index
        </p>
        <p className="text-[18px] font-black leading-tight mb-1" style={{ color: '#eceef5', letterSpacing: '-0.02em' }}>
          Every feature, one map
        </p>
        <p className="text-[12px] leading-relaxed" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {NODES.filter(n => n.r === NR).length} features · {NODES.filter(n => n.r < NR).length} sub-sections · tap any node to open it
        </p>

        {/* Zone legend pills */}
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {ZONES.map(z => (
            <span
              key={z.label}
              className="text-[9px] font-black px-2 py-0.5 rounded-full"
              style={{ background: `${z.color}20`, color: z.color, border: `1px solid ${z.color}38` }}
            >
              {z.label}
            </span>
          ))}
        </div>
      </div>

      {/* ── Diagram ── */}
      <div
        className="rounded-2xl overflow-hidden"
        style={{ border: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto' }}
      >
        <div
          style={{
            position: 'relative',
            width: CW,
            height: CH,
            margin: '0 auto',
            background: 'linear-gradient(180deg, #06070f 0%, #090a17 40%, #06070f 100%)',
          }}
        >

          {/* ── SVG layer: particles, zones, edges, node halos ── */}
          <svg
            viewBox={`0 0 ${CW} ${CH}`}
            preserveAspectRatio="xMidYMin meet"
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          >
            <defs>
              {/* Glow filters */}
              <filter id="aim-glow-sm" x="-60%" y="-60%" width="220%" height="220%">
                <feGaussianBlur stdDeviation="1.5" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="aim-glow-md" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="3.5" />
              </filter>
              <filter id="aim-glow-lg" x="-120%" y="-120%" width="340%" height="340%">
                <feGaussianBlur stdDeviation="7" />
              </filter>
              <filter id="aim-node-halo" x="-100%" y="-100%" width="300%" height="300%">
                <feGaussianBlur stdDeviation="5" />
              </filter>
            </defs>

            {/* Background particles */}
            {PARTICLES.map((p, i) => (
              <circle key={i} cx={p.cx} cy={p.cy} r={p.r} fill="white" opacity={p.o} />
            ))}

            {/* Zone background tints */}
            {ZONES.map(z => (
              <rect key={z.label} x={0} y={z.y1} width={CW} height={z.y2 - z.y1}
                fill={z.color} opacity="0.016" />
            ))}

            {/* Zone divider lines */}
            {ZONES.slice(1).map(z => (
              <line key={`div-${z.label}`}
                x1={10} y1={z.y1} x2={CW - 10} y2={z.y1}
                stroke={z.color} strokeWidth="0.8" opacity="0.18"
                strokeDasharray="4 7" />
            ))}

            {/* Zone labels */}
            {ZONES.map(z => (
              <text key={`ztxt-${z.label}`}
                x={14} y={z.y1 + 13}
                fontSize="7" fontWeight="900" letterSpacing="0.07em"
                fill={z.color} opacity="0.5"
              >
                {z.label}
              </text>
            ))}

            {/* Node halos — SVG circles behind each main node */}
            {NODES.filter(n => n.r === NR).map(node => (
              <React.Fragment key={`nh-${node.id}`}>
                {/* Outer diffuse glow */}
                <circle cx={node.x} cy={node.y} r={NR + 8}
                  fill={node.color} opacity="0.05"
                  filter="url(#aim-node-halo)" />
                {/* Inner soft ring */}
                <circle cx={node.x} cy={node.y} r={NR + 3}
                  fill="none"
                  stroke={node.color} strokeWidth="2" opacity="0.10" />
              </React.Fragment>
            ))}

            {/* ── Edges: outer diffuse glow ── */}
            {EDGES.map(({ f, t, sub }) => {
              if (sub) return null;
              const fn = nodeMap[f]; const tn = nodeMap[t];
              if (!fn || !tn) return null;
              return (
                <path key={`eg-${f}-${t}`}
                  d={bez(fn.x, fn.y, tn.x, tn.y)}
                  fill="none" stroke={fn.color}
                  strokeWidth="6" opacity="0.10"
                  filter="url(#aim-glow-md)" />
              );
            })}

            {/* ── Edges: inner tight glow ── */}
            {EDGES.map(({ f, t, sub }) => {
              if (sub) return null;
              const fn = nodeMap[f]; const tn = nodeMap[t];
              if (!fn || !tn) return null;
              return (
                <path key={`ei-${f}-${t}`}
                  d={bez(fn.x, fn.y, tn.x, tn.y)}
                  fill="none" stroke={fn.color}
                  strokeWidth="2" opacity="0.28"
                  filter="url(#aim-glow-sm)" />
              );
            })}

            {/* ── Edges: sharp line ── */}
            {EDGES.map(({ f, t, sub }) => {
              const fn = nodeMap[f]; const tn = nodeMap[t];
              if (!fn || !tn) return null;
              return (
                <path key={`el-${f}-${t}`}
                  d={bez(fn.x, fn.y, tn.x, tn.y)}
                  fill="none" stroke={fn.color}
                  strokeWidth={sub ? '0.8' : '1.5'}
                  opacity={sub ? '0.18' : '0.50'}
                  strokeDasharray={sub ? '3 4' : undefined}
                />
              );
            })}

            {/* Feedback loop — Analytics → Research */}
            <path
              d={`M 190 1029 C -30 1029, -30 437, 190 437`}
              fill="none" stroke="#f97316"
              strokeWidth="1" opacity="0.13"
              strokeDasharray="5 4"
            />
          </svg>

          {/* ── HTML layer: node buttons + labels + badges ── */}
          {NODES.map(node => {
            const Icon    = node.icon;
            const isSub   = node.r === NR_S;
            const side    = !isSub && node.time ? badgeSide(node) : null;
            const locked  = node.premium && !meetsMinPlan('premium');

            return (
              <React.Fragment key={node.id}>

                {/* Node button */}
                <button
                  onClick={() => handleNodeClick(node)}
                  title={node.label}
                  style={{
                    position: 'absolute',
                    left: node.x - node.r,
                    top: node.y - node.r,
                    width:  node.r * 2,
                    height: node.r * 2,
                    borderRadius: '50%',
                    background: isSub
                      ? `${node.color}0c`
                      : `radial-gradient(circle, ${node.color}28 0%, ${node.color}0e 100%)`,
                    border: isSub
                      ? `1px dashed ${node.color}40`
                      : `1.5px solid ${node.color}90`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                    zIndex: 4,
                    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                    boxShadow: isSub
                      ? 'none'
                      : `0 0 6px ${node.color}50, 0 0 12px ${node.color}20`,
                  }}
                  onMouseEnter={e => {
                    if (isSub) return;
                    e.currentTarget.style.transform    = 'scale(1.18)';
                    e.currentTarget.style.boxShadow    = `0 0 12px ${node.color}70, 0 0 24px ${node.color}35`;
                    e.currentTarget.style.borderColor  = node.color;
                  }}
                  onMouseLeave={e => {
                    if (isSub) return;
                    e.currentTarget.style.transform    = 'scale(1)';
                    e.currentTarget.style.boxShadow    = `0 0 6px ${node.color}50, 0 0 12px ${node.color}20`;
                    e.currentTarget.style.borderColor  = `${node.color}90`;
                  }}
                >
                  <Icon size={isSub ? 8 : 11} color={locked ? '#6b7280' : node.color} />
                  {locked && !isSub && (
                    <div style={{
                      position: 'absolute', top: -3, right: -3,
                      width: 12, height: 12, borderRadius: '50%',
                      background: 'rgba(234,179,8,0.2)',
                      border: '1px solid rgba(234,179,8,0.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Lock size={6} color="#eab308" />
                    </div>
                  )}
                </button>

                {/* Node label — main nodes only; clamped so it never clips off canvas */}
                {!isSub && (
                  <p
                    style={{
                      position: 'absolute',
                      left: Math.max(4, Math.min(CW - 64, node.x - 30)),
                      top: node.y + node.r + 4,
                      width: 60,
                      textAlign: 'center',
                      fontSize: 8,
                      fontWeight: 700,
                      lineHeight: 1.3,
                      color: 'rgba(255,255,255,0.52)',
                      pointerEvents: 'none',
                      zIndex: 4,
                    }}
                  >
                    {node.label}
                  </p>
                )}

                {/* Time badge */}
                {side && (
                  <div
                    style={{
                      position: 'absolute',
                      top: node.y - 11,
                      zIndex: 5,
                      pointerEvents: 'none',
                      ...(side === 'right'
                        ? { left: node.x + node.r + 7 }
                        : { left: node.x - node.r - 7, transform: 'translateX(-100%)' }
                      ),
                    }}
                  >
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                        background: `${node.color}0f`,
                        border: `1px solid ${node.color}35`,
                        borderRadius: 999,
                        padding: '3px 7px',
                        whiteSpace: 'nowrap',
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      <Clock size={8} color={node.color} style={{ opacity: 0.75, flexShrink: 0 }} />
                      <span style={{ fontSize: 9, fontWeight: 800, color: node.color, opacity: 0.9, letterSpacing: '0.01em' }}>
                        {node.time}
                      </span>
                    </div>
                  </div>
                )}

              </React.Fragment>
            );
          })}

          {/* Feedback annotation */}
          <div style={{
            position: 'absolute',
            left: 8, bottom: 10,
            fontSize: 8, fontWeight: 700,
            color: 'rgba(255,255,255,0.16)',
            letterSpacing: '0.03em',
          }}>
            ↺ analytics loops back to research
          </div>

        </div>
      </div>
    </div>
  );
}
